import {
  configureClientRecovery,
  isClientRecoveryEnabled,
  observeClientRecovery,
  type RelayRecoveryObserver,
  reportClientRecoveryError,
} from '@github-ui/ahp-relay/relay-client-recovery'
import {
  getRelayClientRecovery,
  getRelayEntry,
  getRelayStore,
  subscribeRelayRegistry,
} from '@github-ui/ahp-relay/relay-registry'
import type {EstablishedAhpSessionIdentity} from '@github-ui/ahp-session-client/contracts'
import {sendEvent} from '@github-ui/hydro-analytics'

import {browserObservationTime, nextBrowserObservationSequence} from './browser-observation-clock'
import {createClientRecoveryMetricObserver} from './browser-outcome-metrics'
import {CLIENT_RECOVERY_EVENT} from './client-recovery-contract'
import {createClientRecoverySource, recoverySourceRole} from './client-recovery-source'
import {copilotUiNavigationContext} from './copilot-ui-readiness'
import type {UiReadinessSurface} from './ui-readiness-contract'

export interface CopilotRecoveryControls {
  input?: HTMLTextAreaElement | null
  submit?: HTMLButtonElement | null
  stop?: HTMLButtonElement | null
  readOnly: boolean
  actionMode: 'send' | 'steer' | 'queue'
  validInputActionEnabled: boolean
  hasSubmittableContent: boolean
}

let configured = false
let owners = 0
const MAX_RECOVERY_OWNERS = 16

function configure() {
  if (configured || !isClientRecoveryEnabled()) return
  configureClientRecovery(context => {
    const metrics = createClientRecoveryMetricObserver(undefined, () => reportClientRecoveryError('emission'))
    const source = createClientRecoverySource(
      {
        id: () => crypto.randomUUID(),
        now: browserObservationTime,
        nextSequence: nextBrowserObservationSequence,
        role: () => recoverySourceRole(context),
        visibility: () => (document.visibilityState === 'visible' ? 'visible' : 'hidden'),
        emit: fact => {
          metrics.observe(fact)
          sendEvent(
            CLIENT_RECOVERY_EVENT,
            {...fact},
            {
              privacy: 'content_free',
              onError: () => reportClientRecoveryError('emission'),
            },
          )
        },
        reportError: reportClientRecoveryError,
        retire: metrics.dispose,
      },
      context,
    )
    return source
  })
  configured = true
}

function createControlsObserver(onFrame: () => void) {
  const watched = new Map<HTMLElement, {intersecting?: boolean; mutation: MutationObserver}>()
  let frame: number | undefined
  let disposed = false
  const schedule = () => {
    if (disposed || frame !== undefined) return
    frame = requestAnimationFrame(() => {
      frame = undefined
      observeClientRecovery(onFrame)
    })
  }
  const intersection =
    typeof IntersectionObserver === 'function'
      ? new IntersectionObserver(entries => {
          for (const entry of entries) {
            if (!(entry.target instanceof HTMLElement)) continue
            const target = watched.get(entry.target)
            if (target) {
              target.intersecting =
                entry.isIntersecting && entry.intersectionRect.width > 0 && entry.intersectionRect.height > 0
            }
          }
          schedule()
        })
      : undefined

  return {
    schedule,
    watch(element: HTMLElement) {
      const mutation = new MutationObserver(schedule)
      mutation.observe(element, {attributes: true, childList: true, subtree: true})
      watched.set(element, {mutation})
      intersection?.observe(element)
      schedule()
      return () => {
        intersection?.unobserve(element)
        mutation.disconnect()
        watched.delete(element)
      }
    },
    visible(element: HTMLElement) {
      if (document.visibilityState !== 'visible' || !element.isConnected || element.closest('[hidden], [inert]'))
        return false
      try {
        if (
          typeof element.checkVisibility === 'function' &&
          !element.checkVisibility({checkOpacity: true, checkVisibilityCSS: true})
        )
          return false
      } catch {
        reportClientRecoveryError('observer')
        return false
      }
      return intersection ? watched.get(element)?.intersecting === true : true
    },
    dispose() {
      disposed = true
      if (frame !== undefined) cancelAnimationFrame(frame)
      intersection?.disconnect()
      for (const target of watched.values()) target.mutation.disconnect()
      watched.clear()
    },
  }
}

export function createCopilotClientRecoveryOwner(explicitSurface?: UiReadinessSurface) {
  let identity: EstablishedAhpSessionIdentity | undefined
  let viewId: string | undefined
  let source: RelayRecoveryObserver | undefined
  let release: (() => void) | undefined
  let unsubscribe: (() => void) | undefined
  let observer: ReturnType<typeof createControlsObserver> | undefined
  let currentControls: CopilotRecoveryControls | undefined
  let disposed = false
  const visibilityChanged = () => {
    if (document.visibilityState === 'visible') observer?.schedule()
    else source?.unavailable('visibility_interrupted')
  }
  const selectedSurface = () => {
    if (explicitSurface && explicitSurface !== 'unknown') return explicitSurface
    const navigation = copilotUiNavigationContext(new URL(window.location.href, window.location.origin))
    return !navigation?.expectedTaskId || navigation.expectedTaskId === identity?.taskId
      ? navigation?.surface
      : undefined
  }

  const inspect = () =>
    observeClientRecovery(() => {
      const actual = currentControls
      if (!actual?.input || !identity?.taskId || !viewId || !source || !observer || !selectedSurface()) return
      if (!observer.visible(actual.input)) return
      const entry = getRelayEntry(identity.taskId)
      const state = getRelayStore(identity.taskId)?.getSnapshot()
      if (!entry || !state || entry.status !== 'connected' || entry.sessionState?.lifecycle !== 'ready') return
      const role = !entry.passiveKnown
        ? 'unknown'
        : entry.passive
          ? 'passive'
          : actual.readOnly || state.interactivity === 'read-only'
            ? 'read_only'
            : 'writable'
      const blocked = role === 'passive' || role === 'read_only'
      const stopPresent = Boolean(actual.stop?.isConnected)
      const enabled = actual.submit && !actual.submit.disabled && actual.submit.getAttribute('aria-disabled') !== 'true'
      const actionVisible = actual.submit && observer.visible(actual.submit)
      const active = state.activeTurn?.id
      source.controls(viewId, {
        session_id: identity.sessionId,
        environment_id: identity.environmentId,
        chat_id: state.resource,
        role,
        ...(active ? {active_turn_id: active} : {}),
        action_mode: blocked ? 'blocked' : actual.actionMode,
        input_editable: !actual.input.disabled && !actual.input.readOnly,
        send_enabled_for_valid_input: blocked
          ? Boolean(enabled)
          : Boolean(
              actual.validInputActionEnabled &&
              actionVisible &&
              (!actual.hasSubmittableContent || enabled) &&
              (!active || (actual.stop && observer.visible(actual.stop) && !actual.stop.disabled)),
            ),
        stop_present: stopPresent,
      })
    })
  const attach = () =>
    observeClientRecovery(() => {
      if (disposed || !identity?.taskId || !viewId) return
      const surface = selectedSurface()
      if (!surface || !getRelayEntry(identity.taskId)) {
        release?.()
        release = undefined
        source = undefined
        return
      }
      const next = getRelayClientRecovery(identity.taskId, identity, source)
      if (next !== source) {
        release?.()
        source = next
        release = next?.select(viewId, surface)
      }
      observer?.schedule()
    })

  return {
    select(selected: EstablishedAhpSessionIdentity) {
      if (disposed || !isClientRecoveryEnabled()) return false
      identity = selected
      if (!selectedSurface()) return false
      configure()
      if (!viewId) {
        if (owners >= MAX_RECOVERY_OWNERS) {
          reportClientRecoveryError('observation_capacity')
          if (selected.taskId) getRelayClientRecovery(selected.taskId, selected)?.coverageLost('observation_capacity')
          return false
        }
        owners++
        viewId = crypto.randomUUID()
        observer = createControlsObserver(inspect)
        unsubscribe = subscribeRelayRegistry(attach)
        document.addEventListener('visibilitychange', visibilityChanged)
      }
      attach()
      return true
    },
    controls(actual: CopilotRecoveryControls) {
      const currentObserver = observer
      if (disposed || !currentObserver) return
      currentControls = actual
      const cleanups = [actual.input, actual.submit, actual.stop].flatMap(element =>
        element ? [currentObserver.watch(element)] : [],
      )
      currentObserver.schedule()
      return () => {
        if (currentControls === actual) currentControls = undefined
        for (const cleanup of cleanups) cleanup()
      }
    },
    dispose() {
      if (disposed) return
      disposed = true
      release?.()
      unsubscribe?.()
      observer?.dispose()
      document.removeEventListener('visibilitychange', visibilityChanged)
      if (viewId) owners--
      currentControls = undefined
    },
  }
}

export type CopilotClientRecoveryOwner = ReturnType<typeof createCopilotClientRecoveryOwner>
